import React, { useEffect, useRef, useState } from "react";
import {
  IonButton,
  IonCheckbox,
  IonInput,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonTextarea,
} from "@ionic/react";
import "./ContactNum.scss";
import {
  contactParams,
  RespContactInterface,
} from "./Models/ContactNum.model";
import { ContactNumService } from "./ContactNum.service";
import { utilService } from "../../../../services/utilService";
import { addPhoneNumConstants } from "../../../../constants/AddPhoneNumberOptions";
import Loader from "../../../../components/Shared/Loader/Loader";
import { useToast } from "@agney/ir-toast";
import dayjs from "dayjs";
import DateTimePicker from "../../../../components/Shared/DateTimePicker/DateTimePicker";
import { ContactNumAlertService } from "./ContactNumAlert/ContactNumAlert.service";
import moment from "moment";
import { useTranslation } from "react-i18next";

interface ContactNumProps {
  onCancel: () => void;
}

const today = utilService.getCurrentDate();

const ContactNum: React.FC<ContactNumProps> = (props) => {

  const { t, i18n } = useTranslation("settings");
  const Toast = useToast();

  const [isInputEmpty, setIsInputEmpty] = useState(true);
  const [contactData, setContactData]: any = useState([]);
  const [isLoaderActive, setLoaderStatus] = useState(true);
  const [checked, setChecked]: any = useState(false);
  const [message, setMessage]: any = useState("");
  const [selectedTime, setSelectedTime]: any = useState();

  const primaryPhoneInputRef = useRef<HTMLIonInputElement>(null);
  const secondaryPhoneInputRef = useRef<HTMLIonInputElement>(null);

  const [primaryNumberValue, setPrimaryNumberValue]: any = useState("");

  useEffect(() => {
    getContactDetailsToDisplay(params);
  }, []);

  useEffect(() => {
    utilService.hideFooterTab();
    return () => utilService.showFooterTab();
  }, []);

  async function getContactDetailsToDisplay(params: contactParams) {
    setLoaderStatus(true);

    try {
      const [resp, timerResp] = await Promise.all([
        ContactNumService.getContactDetails(params),
        ContactNumAlertService.getContactTimerInformation({ reg_code: utilService.getNetwork() })
      ]);

      if (resp.isSuccess && resp.displayContactContainer?.length > 0) {
        setContactData(resp.displayContactContainer[0]);
        setPrimaryNumberValue(
          resp.displayContactContainer[0].primaryNumber.number
        );
        setChecked(false);
      } else {
        ContactNumService.handleErrorResponse();
      }

      if (timerResp?.isSuccess && timerResp?.result) {
        setChecked(true);
        setMessage(timerResp.result.note);
        setSelectedTime(timerResp.result.time);
        utilService.phoneReminderData = timerResp.result;
      } else if (utilService.phoneReminderData["time"]) {
        setChecked(true);
        setMessage(utilService.phoneReminderData["note"]);
        setSelectedTime(utilService.phoneReminderData["time"]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoaderStatus(false);
    }
  }

  async function saveContactDetails() {

    const pagerValue: any = (document.getElementById("pager") as any)?.value;
    const primaryNumValue: any = (document.getElementById("primary") as any)?.value;
    const secNumValue: any = (document.getElementById("secondary") as any)?.value;
    const typePrimaryValue: any = (document.getElementById("primaryNumber") as any)?.value;
    const typeSecValue: any = (document.getElementById("secondaryNumber") as any)?.value;

    setLoaderStatus(true);

    const params = saveContactDetailsParams(
      pagerValue,
      primaryNumValue,
      secNumValue,
      typePrimaryValue,
      typeSecValue
    );

    let status = false;

    if (checked) {
      if (isPastTime(selectedTime)) {
        utilService.setAlarmTimeOut(utilService.intervalTime);
      }
      status = isPastTime(selectedTime);
    } else {
      ContactNumAlertService.clearTimeOutData();
      utilService.phoneReminderData = {};
    }

    if (!status) {

      const resp = await ContactNumService.saveContactDetails(params);

      if (resp.isSuccess) {
        Toast.success(t("contactUpdatedSuccessfully"));
        if (checked) {
          utilService.phoneReminderData = {
            note: message,
            time: utilService.getDate(selectedTime),
          };
        } else {
          utilService.phoneReminderData = {};
        }

        props.onCancel();
        ContactNumAlertService.contactDetailsUpdate(
          utilService.phoneReminderData
        );

      } else {
        ContactNumService.handleErrorResponse();
      }

      setLoaderStatus(false);

    } else {
      Toast.error(t("validTimeError"));
    }
  }

  const params = {
    reg_code: utilService.getNetwork(),
  };

  const isPastTime = (timerValue: any) => {
    const date: any = moment(timerValue);
    return dayjs().isAfter(date);
  };

  const saveContactDetailsParams = (
    pagerValue: any,
    primaryNumValue: any,
    secNumValue: any,
    typePrimaryValue: any,
    typeSecValue: any
  ) => {

    const contactParams: any = {
      pager: pagerValue,
      reg_code: utilService.getNetwork(),
      primary_number: { number: primaryNumValue, type: typePrimaryValue },
      secondary_number: { number: secNumValue, type: typeSecValue },
    };

    if (checked) {
      contactParams["timer"] = {
        note: message,
        time: dayjs(selectedTime).format("YYYY-MM-DD HH:mm:ss"),
      };
    }

    return contactParams;
  };

  const setTime = (end: any) => {
    setSelectedTime(dayjs(end).format("YYYY-MM-DD HH:mm:ss"));
  };

  const setMessageContent = (e: any) => {
    setMessage(e.detail.value);
  };

  const normalizeInput = (value: string) => {
    const currentValue = value.replace(/[^\d]/g, "");
    const cvLength = currentValue.length;

    if (cvLength < 4) return currentValue;

    if (cvLength < 7)
      return `(${currentValue.slice(0,3)}) ${currentValue.slice(3)}`;

    return `(${currentValue.slice(0,3)}) ${currentValue.slice(3,6)}-${currentValue.slice(6,10)}`;
  };

  const normalizeInputPrimary = () => {
    const value = "" + primaryPhoneInputRef.current!.value;
    primaryPhoneInputRef.current!.value = normalizeInput(value);
  };

  const normalizeInputSecondary = () => {
    const value = "" + secondaryPhoneInputRef.current!.value;
    secondaryPhoneInputRef.current!.value = normalizeInput(value);
  };

  return (
    <div className="contact-num-container">

      <div className="contact-back" onClick={props.onCancel}>
        <div className="contact-back-icon icon-back"></div>
        <div className="contact-back-header">{t("contactDetails")}</div>
      </div>

      <div className="contact-details">

        {isLoaderActive && (
          <div className="loader-container">
            <Loader />
          </div>
        )}

        <form autoComplete="off" noValidate>

          <IonItem>
            <IonLabel position="floating">{t("pager")}</IonLabel>
            <IonInput id="pager" maxlength={15}></IonInput>
          </IonItem>

          <IonItem>
            <IonLabel position="floating">
              <span className="red-star">*</span> {t("primaryPhone")}
            </IonLabel>

            <IonInput
              maxlength={14}
              id="primary"
              value={primaryNumberValue}
              ref={primaryPhoneInputRef}
              onIonInput={(e:any)=>setPrimaryNumberValue(e.target.value)}
              onIonChange={normalizeInputPrimary}
            />
          </IonItem>

          <IonItem>
            <IonLabel position="floating">{t("phoneType")}</IonLabel>

            <IonSelect
              id="primaryNumber"
              okText={t("done")}
              cancelText={t("cancel")}
              placeholder={t("select")}
              value={contactData?.primaryNumber?.type}
            >
              {addPhoneNumConstants?.map((item:any)=>(
                <IonSelectOption value={item.phoneId}>
                  {item.phoneName}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>

          <IonItem>
            <IonLabel position="floating">{t("secondaryPhone")}</IonLabel>

            <IonInput
              maxlength={14}
              id="secondary"
              ref={secondaryPhoneInputRef}
              onIonChange={normalizeInputSecondary}
            />
          </IonItem>

          <IonItem>
            <IonLabel position="floating">{t("phoneType")}</IonLabel>

            <IonSelect
              id="secondaryNumber"
              okText={t("done")}
              cancelText={t("cancel")}
              placeholder={t("select")}
              value={contactData?.secondaryNumber?.type}
            >
              {addPhoneNumConstants?.map((item:any)=>(
                <IonSelectOption value={item.phoneId}>
                  {item.phoneName}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>

        </form>

        <div className="checkbox-wrapper">
          <IonCheckbox
            checked={checked}
            onIonChange={(e)=>setChecked(e.detail.checked)}
          />
          <span>{t("setReminder")}</span>
        </div>

        {checked && (
          <div className="timer-container">

            <div className="timer-label">{t("timer")}</div>

            <DateTimePicker
              minValue={today}
              dateTimeValue={selectedTime}
              getSelectedTime={(end)=>setTime(end)}
            />

            <IonTextarea
              maxlength={60}
              placeholder={t("commentPlaceholder")}
              value={message}
              onIonChange={(e)=>setMessageContent(e)}
            />

          </div>
        )}

      </div>

      <div className="save-button">
        <IonButton
          className="btn primary-btn btn-curved"
          onClick={saveContactDetails}
        >
          {t("save")}
        </IonButton>
      </div>

    </div>
  );
};

export default ContactNum;